import type { Currency, NavMonthlyPoint } from '@nav/shared';
import {
  Area,
  AreaChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingRegion, Skeleton } from '@/components/ui/states';
import type { NavWindow } from '@/lib/api';
import { formatNav, formatNavCompact } from '@/lib/format';
import { BRAND, CHART } from '../palette';

/**
 * “Tren NAV Akhir Bulan” — light fill, timeframe pills.
 *
 * Line colour follows the product, not brand red (see `CHART` in palette.ts):
 * blue for SFL IDR, amber for SFL Dollar. This keeps the two products visually
 * paired with the same hues used in the MoM/YoY combo chart below it.
 * The large NAV figure lives on the KPI row above; this card is the series.
 */

const TIMEFRAMES: NavWindow[] = ['6m', '1y', '2y', 'all'];

interface TooltipPayloadItem {
  value?: number;
}

function NavTooltip({
  active,
  payload,
  label,
  currency,
  hue,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  currency: Currency;
  hue: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const value = payload[0]?.value;
  return (
    <div
      className="px-3.5 py-3 min-w-[140px]"
      style={{ background: '#1A1919', borderRadius: 8, border: '1px solid #1A1919' }}
    >
      <p className="text-xs mb-1" style={{ color: hue, fontWeight: 600 }}>
        {label}
      </p>
      <p className="text-sm tabular-nums" style={{ color: '#FFFFFF', fontWeight: 600 }}>
        NAV: {formatNav(typeof value === 'number' ? value : null, currency)}
      </p>
    </div>
  );
}

function TimeframePills({
  timeframe,
  onChange,
}: {
  timeframe: NavWindow;
  onChange: (tf: NavWindow) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Rentang waktu"
      style={{
        display: 'flex',
        gap: 2,
        padding: 4,
        background: BRAND.track,
        border: `1px solid ${BRAND.panelBorder}`,
        borderRadius: 10,
        flexShrink: 0,
      }}
    >
      {TIMEFRAMES.map((tf) => {
        const isActive = timeframe === tf;
        return (
          <button
            key={tf}
            type="button"
            onClick={() => onChange(tf)}
            aria-pressed={isActive}
            style={{
              padding: '7px 12px',
              borderRadius: 7,
              fontSize: 11,
              fontWeight: isActive ? 600 : 500,
              border: 'none',
              cursor: 'pointer',
              letterSpacing: '0.02em',
              background: isActive ? BRAND.panel : 'transparent',
              color: isActive ? BRAND.red : BRAND.textDim,
              boxShadow: isActive ? 'inset 0 0 0 1px rgba(234,27,26,0.30)' : 'none',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {tf.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

export function NavPerformanceCard({
  points,
  currency,
  timeframe,
  onTimeframeChange,
  isLoading,
  error,
  onRetry,
}: {
  points: NavMonthlyPoint[];
  currency: Currency;
  timeframe: NavWindow;
  onTimeframeChange: (tf: NavWindow) => void;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const header = (
    <div className="px-7 pt-6 pb-2 flex items-center justify-between gap-3 flex-wrap">
      <p
        className="uppercase"
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: BRAND.textFaint,
          fontFamily: 'var(--font-sans)',
        }}
      >
        NAV Akhir Bulan
      </p>
      <TimeframePills timeframe={timeframe} onChange={onTimeframeChange} />
    </div>
  );

  if (isLoading) {
    return (
      <Card>
        {header}
        <LoadingRegion label="Memuat grafik NAV" className="px-7 pt-4 pb-7">
          <Skeleton height={280} radius={10} />
        </LoadingRegion>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        {header}
        <ErrorState error={error} onRetry={onRetry} />
      </Card>
    );
  }

  if (points.length === 0) {
    return (
      <Card>
        {header}
        <EmptyState message="Belum ada data NAV untuk produk ini. Unggah berkas NAV bulanan untuk mengisinya." />
      </Card>
    );
  }

  const navValues = points.map((d) => d.navClose);
  const navMin = Math.min(...navValues);
  const navMax = Math.max(...navValues);
  const navPad = Math.max((navMax - navMin) * 0.18, navMax * 0.002, 0.0001);
  const pointCount = points.length;
  const labelStep = pointCount > 20 ? 4 : pointCount > 12 ? 3 : pointCount > 7 ? 2 : 1;

  // Product-specific hue, not brand red — see CHART in palette.ts.
  const hue = currency === 'IDR' ? CHART.idr.strong : CHART.usd.strong;
  const tooltipHue = currency === 'IDR' ? CHART.idr.soft : CHART.usd.strong;
  const gradientId = currency === 'IDR' ? 'navGradIdr' : 'navGradUsd';

  const renderNavLabel = (props: {
    x?: string | number;
    y?: string | number;
    value?: string | number;
    index?: number;
  }) => {
    const { x, y, value, index } = props;
    if (x == null || y == null || index == null) return null;
    const isLast = index === pointCount - 1;
    if (!isLast && index % labelStep !== 0) return null;
    return (
      <text
        x={Number(x)}
        y={Number(y) - 10}
        textAnchor="middle"
        fontSize={9}
        fontWeight={700}
        fill={hue}
        fontFamily="var(--font-sans)"
      >
        {formatNavCompact(Number(value), currency)}
      </text>
    );
  };

  return (
    <Card>
      {header}
      <div style={{ height: 330, padding: '4px 12px 16px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 28, right: 22, left: 12, bottom: 6 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={hue} stopOpacity={0.18} />
                <stop offset="100%" stopColor={hue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(27,36,54,0.08)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#6B7488', fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              padding={{ left: 18, right: 18 }}
              interval={labelStep - 1}
              tickFormatter={(v: string) => {
                const parts = v.split(' ');
                return `${parts[0]} '${parts[1]?.substring(2) ?? ''}`;
              }}
            />
            <YAxis
              hide
              domain={[() => navMin - navPad, () => navMax + navPad]}
            />
            <Tooltip
              content={<NavTooltip currency={currency} hue={tooltipHue} />}
              cursor={{ stroke: hue, strokeWidth: 1, strokeDasharray: '4 4' }}
            />
            <Area
              type="monotone"
              dataKey="navClose"
              stroke={hue}
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              dot={{ r: 4, fill: '#fff', stroke: hue, strokeWidth: 2 }}
              activeDot={{ r: 6, fill: hue, stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive={false}
            >
              <LabelList dataKey="navClose" content={renderNavLabel} />
            </Area>
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
